import { api } from '../api/client';
import type { PlanRequest, RoutePlan, TripRequest } from '../api/types';

/**
 * One day of an operation, as the single-day request that produced it.
 *
 * The export asks the router for turn-by-turn against the request a plan was
 * made from, so the words follow the line that was drawn. An operation has no
 * such request per day: it has defaults and a list of overrides, and the
 * server merges them. This does the same merge on this side.
 *
 * Only what the directions call actually reads has to be right — the costing,
 * the road answers, the toll preference and the two ends. The rest is filled
 * in to satisfy the type, and is never looked at: the stop ORDER comes from
 * the plan that already exists, not from planning the day again.
 */
export function dayPlanRequest(
  request: TripRequest,
  dayIndex: number,
  plan: RoutePlan,
): PlanRequest | null {
  const day = request.days[dayIndex];
  const base = request.defaults;

  /*
   * The ends as the plan actually used them.
   *
   * Taken from the plan rather than from the request, because a day may say
   * nothing about where it starts — "wherever yesterday finished" — and the
   * only thing that knows where that was is the day that came back.
   */
  const first = plan.stops[0];
  const last = plan.stops[plan.stops.length - 1];
  if (!first || !last) return null;

  const ref = (p: { poiId: string | null; lat: number; lng: number }) => (
    p.poiId
      ? { kind: 'poi' as const, poiId: p.poiId }
      : { kind: 'coords' as const, lat: p.lat, lng: p.lng }
  );

  const routing = day?.routingOptions ?? base.routingOptions;
  const theme = day?.themeKey;
  const tolls = base.avoidTolls;

  return {
    cityId: day?.cityId ?? request.cityId,
    start: ref(first),
    end: ref(last),
    mode: day?.mode ?? base.mode,
    ...(day?.date ? { date: day.date } : {}),
    ...(theme ? { themeKey: theme } : {}),
    ...(routing ? { routingOptions: routing } : {}),
    ...(tolls === undefined ? {} : { avoidTolls: tolls }),
    mustVisitPoiIds: [],
    interestCategoryKeys: [],
    avoid: base.avoid ?? { poiIds: [] },
    safetyPreference: base.safetyPreference ?? 'off',
  };
}

/**
 * Asks the router for the turns of a plan that is already drawn.
 *
 * Shared by the itinerary's "Turn-by-turn" and by the export, which want the
 * same answer for the same reason: the words belong to the line on screen,
 * so they are fetched against the request that drew it and the stop order it
 * settled on.
 */
export function directionsForPlan(request: PlanRequest, plan: RoutePlan) {
  return () => api.directions(
    request,
    plan.stops.map((s) => ({ lat: s.lat, lng: s.lng })),
    plan.avoidedZones.map((z) => z.polygon),
  );
}
